import { useState, useEffect, useRef, MutableRefObject, useCallback } from 'react';

interface IntersectionObserverOptions {
  threshold?: number | number[];
  root?: Element | Document | null;
  rootMargin?: string;
  freezeOnceVisible?: boolean;
}

export function useIntersectionObserver({
  threshold = 1,
  root = null,
  rootMargin = '0px',
  freezeOnceVisible = true,
}: IntersectionObserverOptions): [MutableRefObject<null> | undefined, boolean, () => void] {
  const [isVisible, setIsVisible] = useState<boolean>(false);
  const elementRef = useRef(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const cleanupObserver = () => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
  };

  const reset = useCallback(() => {
    setIsVisible(false);
    cleanupObserver();
    observeElement();
  }, []);

  const observeElement = () => {
    if (elementRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries, observerInstance) => {
          const entry = entries[0];
          if (entry.isIntersecting) {
            setIsVisible(true);

            if (freezeOnceVisible) {
              observerInstance.disconnect();
            }
          }
        },
        { root, rootMargin, threshold }
      );

      observerRef.current.observe(elementRef.current);
    }
  };

  useEffect(() => {
    observeElement();

    return () => {
      cleanupObserver();
    };
  }, [threshold, root, rootMargin]);

  return [elementRef, isVisible, reset];
}
export default useIntersectionObserver;
